import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Croix } from './Icone.tsx'

/**
 * Toast — une seule voix système à la fois.
 *
 * C'est le mécanisme d'annulation de l'application. Une suppression n'ouvre
 * PAS de dialogue de confirmation : un dialogue qu'on voit souvent finit par
 * être validé sans être lu, et il coûte un geste à chaque fois, y compris les
 * neuf fois sur dix où l'on était sûr de soi. On agit tout de suite, et on
 * offre de revenir en arrière.
 *
 * Fond encre sur texte crème : un toast n'est pas une surface de
 * l'application, c'est une voix qui s'adresse à elle.
 *
 * Rendu par PORTAIL : il est déclenché depuis des feuilles, qui portent un
 * `transform` — et un ancêtre transformé devient le bloc conteneur de tout
 * `position: fixed`. Sans portail, le toast se calerait sur la feuille.
 */

export type Annonce = { id: string; texte: string; action?: { label: string; faire: () => void } }

let poser: ((a: Annonce) => void) | null = null

/** Appelable de n'importe où, y compris hors d'un composant React. */
export function annoncer(texte: string, action?: Annonce['action']): void {
  poser?.({ id: crypto.randomUUID(), texte, action })
}

export function Toasts() {
  const [file, setFile] = useState<Annonce[]>([])
  const minuteries = useRef(new Map<string, number>())

  useEffect(() => {
    poser = (a) => setFile((f) => [...f.slice(-1), a])
    return () => {
      poser = null
    }
  }, [])

  const retirer = (id: string) => setFile((f) => f.filter((a) => a.id !== id))

  useEffect(() => {
    for (const a of file) {
      if (minuteries.current.has(a.id)) continue
      /*
       * 6 secondes, et pas 3 : le toast porte une ANNULATION. Il faut le
       * temps de lire, de comprendre qu'on s'est trompé, et de viser.
       */
      const t = window.setTimeout(() => retirer(a.id), 6000)
      minuteries.current.set(a.id, t)
    }
    // Le minuteur ne doit pas courir pendant que l'app est en arrière-plan :
    // on reviendrait sur un toast déjà parti sans l'avoir vu.
    const pause = () => {
      if (document.visibilityState !== 'hidden') return
      for (const t of minuteries.current.values()) clearTimeout(t)
      minuteries.current.clear()
    }
    document.addEventListener('visibilitychange', pause)
    return () => document.removeEventListener('visibilitychange', pause)
  }, [file])

  if (file.length === 0) return null

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-3 z-[90] flex flex-col gap-2"
      style={{ bottom: 'calc(var(--size-bar) + env(safe-area-inset-bottom, 0px) + 12px)' }}
    >
      {file.map((a) => (
        <div
          key={a.id}
          role="status"
          /*
           * TRANSITION, pas keyframe. Les toasts s'ajoutent en rafale — une
           * suppression suivie d'une annulation, par exemple. Une keyframe
           * repart de zéro à chaque interruption ; une transition se
           * re-cible depuis la valeur courante.
           *
           * @starting-style donne l'état de départ sans passer par un
           * useEffect qui bascule `mounted` après le premier rendu.
           */
          className="toast-entree pointer-events-auto flex items-center gap-3 rounded-full bg-ink px-4 py-3 text-canvas shadow-pop"
        >
          <span className="min-w-0 flex-1 truncate text-meta">{a.texte}</span>
          {a.action && (
            <button
              type="button"
              onClick={() => {
                a.action!.faire()
                retirer(a.id)
              }}
              className="pressable shrink-0 text-meta font-semibold"
            >
              {a.action.label}
            </button>
          )}
          <button
            type="button"
            aria-label="Fermer"
            onClick={() => retirer(a.id)}
            className="pressable shrink-0 opacity-60"
          >
            <Croix className="size-4" />
          </button>
        </div>
      ))}
    </div>,
    document.body,
  )
}
